import { DateTime } from "luxon";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { APSSForm } from "@/Patient/Models/Forms/Apss/APSSForm";
import { APSSPatientDTO } from "@/Patient/Models/Forms/Apss/APSSPatientDTO";
import { APSSFormInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { MyAPSSFormTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { ApssFilterType } from "@/Portfolio/Models/UI/IAPSSInboxModel";

/**
 * An API client for the APSS API.
 */
class ApssClient {
    private static readonly actionRequiredSpecificFilter = ('[[["etat_code", "=", "255599008"], "or" ,["etat_code", "=", "29179001"], "or" ,["etat_code", "=", "103327009"]], "and" ,[["sousEtat_code", "=", "281331007"], "or" ,["sousEtat_code", "=", "281323002"], "or" ,["sousEtat_code", "=", "184179006"], "or" ,["sousEtat_code", "=", "55171000087107"], "or" ,["sousEtat_code", "=", "54791000087109"], "or" ,["sousEtat_code", "=", "54761000087101"], "or" ,["sousEtat_code", "=", "54821000087102"]]]');
    private static readonly conseilNumeriqueSpecificFilter = ('[[["etat_code", "=", "443942000"], "or" ,["etat_code", "=", "385642001"]], "and" ,[["sousEtat_code", "=", "281331007"], "or", ["sousEtat_code", "=", "371151008"], "or" ,["sousEtat_code", "=", "408408004"], "or" ,["sousEtat_code", "=", "185004005"]]]');
    public static async post(path: string, specificHistoricalId: string = null): Promise<any> {
        const { token } = await sessionStore.getAPSSToken(null, specificHistoricalId);

        if (!token)
            return;

        const response = await Client.postAsync<{ jsonDTO: string }>(
            ServerOperations.Server.Apss_FowardToApss,
            {
                url: path,
                ApssToken: token
            }
        );

        if (!response.isSuccess)
            return;

        // HACK: this is a hotfix because there's newline character in the response
        const result = response.data.jsonDTO?.replace(/[\r\n]+/g, " ");
        return JSON.parse(result);
    }

    /**
     * Fetch all forms from the APSS API.
     */
    public static async fetchAllForms(): Promise<APSSForm[]> {
        const response = await ApssClient.post("/Requetes/api/rest/v1/formulaires");
        return response.Formulaires || [];
    }

    /**
     * Fetch a form from the APSS API.
     * @param id The APSS form ID.
     */
    public static async fetchForm(id: number, historicalId: string): Promise<APSSFormInboxItem> {
        try {
            const response = await ApssClient.post(`/Requetes/api/rest/v1/requetes/${id}`, historicalId);
            return MyAPSSFormTableItem.DecodeHtmlCaracters<APSSFormInboxItem>(response.infoRequete);
        }
        catch (error) {
            throw cacheStore.GetString("CRDSSyncError");
        }
    }

    public static createForm(formCode: string, patient: APSSPatientDTO): void {
        ApssClient.openFormFromUrl(`/Requetes/api/web/v1/formulaires/${formCode}`, patient, false);
    }

    public static printForm(formId: number, historicalId: string): void {
        ApssClient.openFormFromUrl(`/Requetes/api/rest/v1/requetes/${formId}/impression`, null, true, historicalId);
    }

    public static openForm(formId: string, historicalId: string): void {
        ApssClient.openFormFromUrl(`/Requetes/api/web/v1/requetes/${formId}`, null, false, historicalId);
    }

    public static async openFormFromUrl(url: string, patient?: APSSPatientDTO, isPopup?: boolean, historicalId: string = null): Promise<void> {
        const { token, patientDtoFilePath } = await sessionStore.getAPSSToken(patient, historicalId);
        const urlWithAuth = [`https://${cacheStore.SafirApssURL}${url}`, $.param({ APSSToken: token, PatientDtoFilePath: patientDtoFilePath })].join("?");

        if (isPopup)
            MyleHelper.OpenNewWindow(urlWithAuth);
        else
            window.open(urlWithAuth, "_blank");
    }

    public static getAPSSRequestCount(conseilNumeriqueOnly: boolean, actionRequiredOnly: boolean): string {
        let columns = [
            "ReqReg_Id"
        ];
        let request = "/Requetes/api/rest/v1/getNbRequetes?";
        request += "nColumns=" + columns.length + "&";

        columns.forEach((x, i) => {
            request += "Column_" + i + "=" + x + "&";
        });
        request += "PageNumber=1&PageLength=1";

        const currentEmployee = sessionStore.getCurrentEmployee().Model;
        if (Employee.shouldHaveSupervisorBasedOnTitle(currentEmployee))
            request += "&Filter_ReqReg_CleCreateur=" + currentEmployee.UserName + "%40" + cacheStore.ClientIdentifier + ".medfarsolutions.com";
        else
            request += "&Filter_noPermisRef=" + Employee.getCleanLicenseNumber(currentEmployee);

        if (conseilNumeriqueOnly) {
            request += "&Filter_ReqReg_IdType=1001";
            request += "&Filter_dmeReserve_archiveFlag=0";
            request += "&filtresDxDatagrid=" + btoa(this.conseilNumeriqueSpecificFilter);
        }

        if (actionRequiredOnly) {
            let dataGridFilter = btoa(this.actionRequiredSpecificFilter);
            request += "&filtresDxDatagrid=" + dataGridFilter;
            request += "&Filter_dmeReserve_archiveFlag=0";
        }

        return request;
    }

    public static ApssGridColumns = [
        "nom",
        "prenom",
        "DDN",
        "ReqReg_Id",
        "ReqReg_EtablissementCreateur",
        "nomFormulaireLongue",
        "ReqReg_DateCreation",
        "ReqReg_DateModification",
        "ReqReg_Etat",
        "ReqReg_SousEtat",
        "ReqReg_CipCreateur",
        "ReqReg_NomCreateur",
        "ReqReg_CleCreateur",
        "lstPrerequis",
        "lstPrerequisManquant",
        "nomRef",
        "prenomRef",
        "NAM",
        "texteNumDossierDME",
        "ReqReg_DateChangementEtat",
        "sousEtat_texte",
        "changementEtat_Detail",
        "dmeReserve_archiveFlag",
        "ReqReg_IdType",
        "etat_code",
        "sousEtat_code",
        "etat_texte",
        "saFHIR_infoSupplementaireCRDS",
        "dmeReserve_displayFlag"
    ];

    public static buildBaseRequestQueryUrl(
        filterType: ApssFilterType,
        employee: Employee,
        currentPage: number,
        pageSize: number
    ): string {
        let columns = ApssClient.ApssGridColumns;

        let request = "/Requetes/api/rest/v1/requetes?";

        // Select Columns
        request += "nColumns=" + columns.length + "&";
        columns.forEach((x, i) => {
            request += "Column_" + i + "=" + x + "&";
        });

        // Add paging
        request += "PageNumber=" + (currentPage) + "&PageLength=" + pageSize + "&nSortCols=1&SortCol=ReqReg_Etat";

        // Employee Filter
        if (employee != null) {
            if (filterType == ApssFilterType.ParCreateur)
                request += "&Filter_ReqReg_CleCreateur=" + encodeURIComponent(`${employee.UserName}@${cacheStore.ClientIdentifier}.medfarsolutions.com`);
            else
                request += "&Filter_noPermisRef=" + Employee.getCleanLicenseNumber(employee);
        }

        return request;
    }

    public static getRecentRequests(
        filterType: ApssFilterType,
        employee: Employee,
        currentPage: number,
        pageSize: number,
        showRequestWithMissingAttachmentsOnly: boolean,
        statuses: Immutable.List<CheckboxUIModel>,
        searchText: string
    ): string {
        let request = ApssClient.buildBaseRequestQueryUrl(filterType, employee, currentPage, pageSize);

        // Status filter
        let checkedStatus = statuses.filter(x => x.Checked == true).toArray();
        if (checkedStatus.length > 0)
            request += "&Filter_ReqReg_Etat=" + checkedStatus[0].Id;

        // Missing Attachment Filter
        if (showRequestWithMissingAttachmentsOnly)
            request += "&Filter_prerequisManquant=vrai";

        // Text Filter
        if (!IsNullOrWhiteSpace(searchText))
            request += "&Search=" + encodeURIComponent(searchText);

        return request;
    }

    public static getRequestWithAdvancedFilters(
        filterType: ApssFilterType,
        employee: Employee,
        currentPage: number,
        pageSize: number,
        searchText: string,
        showArchivedRequest: boolean
    ): string {
        let request = ApssClient.buildBaseRequestQueryUrl(filterType, employee, currentPage, pageSize);

        if (!showArchivedRequest)
            request += "&Filter_dmeReserve_archiveFlag=0";

        if (!IsNullOrWhiteSpace(searchText))
            request += "&Search=" + encodeURIComponent(searchText);

        // Sort Direction
        let sortColumnIndex = ApssClient.ApssGridColumns.indexOf("ReqReg_DateChangementEtat");
        request += `&nSortCols=1&SortCol_0=${sortColumnIndex}&SortDir_0=desc`;

        let dataGridFilter = btoa(this.actionRequiredSpecificFilter);
        request += "&filtresDxDatagrid=" + dataGridFilter;

        return request;
    }

    public static async ArchiveRequest(apssFormId: string, historiqueId: string): Promise<ClientResponse<any>> {
        const authResponse = await Client.postAsync<any>(
            ServerOperations.Server.Myle_SetApssCookie,
            {
                UserDisplayName: sessionStore.getCurrentEmployee().nameWithPrefix(),
                ClinicHistoricId: historiqueId
            }
        );

        return Client.postAsync<any>(ServerOperations.Server.Myle_ForwardApssPatchRequest, {
            ApssFormId: apssFormId,
            ApssToken: authResponse.data.Cookie,
            PartialFormDataJson: {
                infoRequete: {
                    dmeReserve_archiveFlag: ["1"]
                    // dmeReserve_archiveDate : DateTime.utc()
                }
            }
        });
    }

    public static async RestoreRequest(apssFormId: string, historiqueId: string): Promise<ClientResponse<any>> {
        const authResponse = await Client.postAsync<any>(
            ServerOperations.Server.Myle_SetApssCookie,
            {
                UserDisplayName: sessionStore.getCurrentEmployee().nameWithPrefix(),
                ClinicHistoricId: historiqueId
            }
        );

        return Client.postAsync<any>(ServerOperations.Server.Myle_ForwardApssPatchRequest, {
            ApssFormId: apssFormId,
            ApssToken: authResponse.data.Cookie,
            PartialFormDataJson: {
                infoRequete: {
                    dmeReserve_archiveFlag: ["0"],
                    dmeReserve_archiveDate: [DateTime.now().toISO()]
                }
            }
        });
    }

    public static async RemoveFromActionRequired(apssFormId: string, historiqueId: string): Promise<ClientResponse<any>> {
        const authResponse = await Client.postAsync<any>(
            ServerOperations.Server.Myle_SetApssCookie,
            {
                UserDisplayName: sessionStore.getCurrentEmployee().nameWithPrefix(),
                ClinicHistoricId: historiqueId
            }
        );

        return Client.postAsync<any>(ServerOperations.Server.Myle_ForwardApssPatchRequest, {
            ApssFormId: apssFormId,
            ApssToken: authResponse.data.Cookie,
            PartialFormDataJson: {
                infoRequete: {
                    dmeReserve_displayFlag: ["0"]
                }
            }
        });
    }

    public static fetchAllConseilNumeriqueForms(
        filterType: ApssFilterType,
        employee: Employee,
        currentPage: number,
        pageSize: number,
        searchText: string,
        showArchivedRequest: boolean
    ): string {
        let request = ApssClient.buildBaseRequestQueryUrl(filterType, employee, currentPage, pageSize);

        // Sort Direction
        let sortColumnIndex = ApssClient.ApssGridColumns.indexOf("ReqReg_DateChangementEtat");
        request += `&nSortCols=1&SortCol_0=${sortColumnIndex}&SortDir_0=desc`;

        // Only take Conseil Numerique forms, IdType = 1001
        request += "&Filter_ReqReg_IdType=1001";

        if (!showArchivedRequest)
            request += "&Filter_dmeReserve_archiveFlag=0";

        if (!IsNullOrWhiteSpace(searchText))
            request += "&Search=" + encodeURIComponent(searchText);

        return request;
    }
}

export { ApssClient };
